
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User } from 'lucide-react';
import { type Gift, type Brand } from '@/utils/types';
import ContactsSelectionDialog from '../ContactsSelectionDialog';
import { toast } from '@/hooks/use-toast';
import { type Contact } from '../contacts/types';

interface RegiftActionProps {
  selectedGift: Gift;
  targetEmail: string;
  brands: Brand[];
  onTargetEmailChange: (email: string) => void;
  onAction: () => void;
}

const RegiftAction: React.FC<RegiftActionProps> = ({
  selectedGift,
  targetEmail,
  brands,
  onTargetEmailChange,
  onAction
}) => {
  const [showContactsDialog, setShowContactsDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  
  // Calculate fee and net amount
  const feeAmount = selectedGift.amount * 0.10;
  const netAmount = selectedGift.amount - feeAmount;
  
  const getBrandName = (brandId: string) => {
    const brand = brands.find(b => b.id === brandId);
    return brand ? brand.name : 'Unknown';
  };
  
  const handleSelectContact = (contact: Contact) => {
    console.log("Contact selected:", contact);
    setSelectedContact(contact);
    onTargetEmailChange(contact.email);
    setShowContactsDialog(false);
  };

  // Show contacts dialog automatically when component mounts if no email is set
  useEffect(() => {
    if (!targetEmail && !selectedContact) {
      console.log("Auto-opening contacts dialog because no recipient is set");
      setShowContactsDialog(true);
    }
  }, [targetEmail, selectedContact]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Send your GivGo Bucks to a friend.
      </p>
      <div className="space-y-4">
        <div className="p-3 border rounded-lg bg-purple-50">
          <p className="font-medium text-center">Sending ${netAmount.toFixed(2)} {getBrandName(selectedGift.brandId)} GivGo Bucks</p>
          <p className="text-xs text-center text-muted-foreground mt-1">10% transaction fee applies (${feeAmount.toFixed(2)})</p>
        </div>
        
        {selectedContact ? (
          <div className="p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-givgo-light flex items-center justify-center">
                {selectedContact.avatar ? (
                  <img src={selectedContact.avatar} alt={selectedContact.name} className="h-10 w-10 rounded-full" />
                ) : (
                  <User className="h-5 w-5 text-givgo-purple" />
                )}
              </div>
              <div>
                <p className="font-medium">{selectedContact.name}</p>
                <p className="text-sm text-muted-foreground">{selectedContact.email}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-auto"
                onClick={() => setShowContactsDialog(true)}
              >
                Change
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Button 
              className="w-full bg-givgo-purple hover:bg-givgo-purple/90"
              onClick={() => setShowContactsDialog(true)}
            >
              Select Contact
            </Button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-background px-2 text-muted-foreground">OR</span>
              </div>
            </div>
            
            <div>
              <Label htmlFor="email" className="block text-sm font-medium mb-1">Enter Email Manually</Label>
              <Input
                id="email"
                type="email"
                placeholder="friend@example.com"
                value={targetEmail}
                onChange={(e) => onTargetEmailChange(e.target.value)}
              />
            </div>
          </div>
        )}
        
        {/* Removed the redundant "Send Gift" button that was here */}
      </div>
      
      <ContactsSelectionDialog 
        open={showContactsDialog}
        onOpenChange={setShowContactsDialog}
        onSelectContact={handleSelectContact}
      />
    </div>
  );
};

export default RegiftAction;
